<template>
  <v-dialog v-model="dialog" scrollable :fullscreen="isFullscreen" max-width="800px">
    <v-card class="agent-dialog-card" :style="{ height: isFullscreen ? '100vh' : '80vh' }">
      <v-card-title class="pa-4 d-flex justify-space-between flex-shrink-0">
        <div class="d-flex align-center">
          <app-icon class="mr-2" size="32">wand_stars</app-icon>
          {{ title }}
        </div>
        <div class="d-flex align-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" @click="handleClearChat" class="rounded-xl mr-1"
                icon small :color="clearChatConfirming ? 'error' : undefined">
                <app-icon size="16" :class="{ 'text-medium-emphasis': !clearChatConfirming }">
                  {{ clearChatConfirming ? 'warning' : 'delete_sweep' }}
                </app-icon>
              </v-btn>
            </template>
            <span>{{ clearChatConfirming ? 'Clique novamente para confirmar' : 'Limpar conversa' }}</span>
          </v-tooltip>
          <v-tooltip bottom v-if="!$vuetify.breakpoint.smAndDown">
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" @click="fullscreenToggled = !fullscreenToggled"
                class="rounded-xl mr-1" icon small>
                <app-icon size="16" class="text-medium-emphasis">
                  {{ fullscreenToggled ? 'fullscreen_exit' : 'fullscreen' }}
                </app-icon>
              </v-btn>
            </template>
            <span>{{ fullscreenToggled ? 'Sair da tela cheia' : 'Tela cheia' }}</span>
          </v-tooltip>
          <v-btn @click="close" class="rounded-xl" icon>
            <app-icon size="18">close</app-icon>
          </v-btn>
        </div>
      </v-card-title>
      <v-divider />
      <div class="agent-dialog-body">
        <AiAgentChat
          :messages="renderedMessages"
          :loading="loading"
          :suggestions="suggestions"
          :instructions.sync="form.instructions"
          :credits-remaining="creditsRemaining"
          :disabled="creditsExhausted"
          @send="sendMessage"
          @use-suggestion="useSuggestion"
          @decide="handleDecide"
        />
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import AiAgentChat from './AiAgentChat.vue'
import { toWireDocument, fromWireDocument, applyChange } from './adapters/documentAdapters'
import { DEFAULT_TEXT_SUGGESTIONS as DEFAULT_SUGGESTIONS } from '../constants'

/**
 * Reusable "agent with chat" dialog. Public API is intentionally shaped to
 * drop in wherever the old TextGenerationFormSection was used:
 *
 *   this.$refs.AiAgentDialog.open({
 *     agent: 'document_editor',
 *     contextType: 'customer_professional_record',
 *     contextId: this.customer.id,
 *     document: this.customerProfessionalRecord.professional_record,
 *   })
 *
 * and it emits @apply with the full updated document, matching the
 * existing setText(text) handlers exactly.
 *
 * It never calls a save endpoint itself — only the parent form's own
 * "Salvar" persists. The backend enforces the same rule from its side:
 * there is no write tool, only propose_document_changes.
 */
export default {
  name: 'AiAgentDialog',
  components: { AiAgentChat },
  data() {
    return {
      dialog: false,
      loading: false,
      agent: null,
      contextType: null,
      contextId: null,
      // Optional: a Customer id to attach for extra read-only tool access
      // (histórico de sessões, perfil, anamnese) on a context that isn't
      // itself owned by a customer — e.g. drafting a document for one.
      customerId: null,
      title: 'Editar com I.A',
      suggestions: DEFAULT_SUGGESTIONS,
      conversationId: null,
      messages: [],
      workingDocument: [],
      // Images stripped out of an html-mode document by toWireDocument, to
      // restore before handing the document back to the parent form.
      documentImages: [],
      pendingFeedback: [],
      form: {
        instructions: null,
      },
      // { used, total } in I.A. credits, as returned by ai-assistant/usage
      // and refreshed after every turn — null until the first load resolves.
      credits: null,

      // Click-again-to-confirm on the (discreet, icon-only) clear-chat button.
      clearChatConfirming: false,
      clearChatConfirmTimer: null,

      // Manual fullscreen toggle — useful for a longer document/message,
      // separate from the automatic fullscreen on small screens below.
      fullscreenToggled: false,
    }
  },
  computed: {
    isFullscreen() {
      return this.$vuetify.breakpoint.smAndDown || this.fullscreenToggled
    },
    creditsRemaining() {
      return this.credits ? Math.max(this.credits.total - this.credits.used, 0) : null
    },
    creditsExhausted() {
      return this.creditsRemaining !== null && this.creditsRemaining <= 0
    },
    renderedMessages() {
      return this.messages.map((message) => ({
        ...message,
        actions: (message.actions || []).map((action) => ({
          ...action,
          changes: (action.changes || []).map((change) => ({
            ...change,
            status: change.status || 'pending',
            stale: this.isStale(change),
          })),
        })),
      }))
    },
  },
  methods: {
    open(options) {
      this.agent = options.agent
      this.contextType = options.contextType
      this.contextId = options.contextId
      this.customerId = options.customerId || null
      this.title = options.title || 'Editar com I.A'
      this.suggestions = options.suggestions || DEFAULT_SUGGESTIONS

      // Deep clone: the parent's array is bound live to its own textareas,
      // and must not be mutated until the professional explicitly accepts
      // a change (see handleDecide/emitApply).
      const wire = toWireDocument(JSON.parse(JSON.stringify(options.document || [])), this.contextType)
      this.workingDocument = wire.document
      this.documentImages = wire.images

      this.messages = []
      this.conversationId = null
      this.pendingFeedback = []
      this.form.instructions = null
      this.loading = false
      this.clearChatConfirming = false
      clearTimeout(this.clearChatConfirmTimer)

      this.dialog = true

      this.loadHistory()

      this.loadCredits()
    },

    close() {
      this.dialog = false
      this.$emit('close')
    },

    handleClearChat() {
      if (this.clearChatConfirming) {
        clearTimeout(this.clearChatConfirmTimer)
        this.clearChatConfirming = false
        this.clearChat()
        return
      }

      this.clearChatConfirming = true
      this.clearChatConfirmTimer = setTimeout(() => {
        this.clearChatConfirming = false
      }, 4000)
    },

    // Only clears the conversation view — the document itself (and any
    // change already accepted into workingDocument) is untouched. The next
    // message starts a brand new conversation; the old one is simply no
    // longer referenced, not deleted.
    clearChat() {
      this.messages = []
      this.conversationId = null
      this.pendingFeedback = []
    },

    loadHistory() {
      this.$http
        .$get('ai-assistant/agent/conversation', {
          agent: this.agent,
          context_type: this.contextType,
          context_id: this.contextId,
        })
        .then((response) => {
          if (response && response.conversation) {
            this.conversationId = response.conversation.id
            this.messages = (response.messages || []).map((message) => ({
              id: message.id,
              role: message.role,
              text: message.message,
              actions: message.actions || [],
              warnings: message.warnings || [],
            }))
          }
        })
        .catch(() => {})
    },

    loadCredits() {
      this.$http
        .$get('ai-assistant/usage')
        .then((response) => {
          if (response && response.data) {
            this.credits = { used: response.data.ia_used_credits, total: response.data.ia_credits }
          }
        })
        .catch(() => {})
    },

    useSuggestion(suggestion) {
      if (this.creditsExhausted) {
        return
      }

      this.form.instructions = suggestion
      this.sendMessage()
    },

    sendMessage() {
      if (this.loading || this.creditsExhausted || !this.form.instructions || !this.form.instructions.length) {
        return
      }

      const text = this.form.instructions
      this.form.instructions = null

      this.messages.push({ id: 'local-user-' + Date.now(), role: 'user', text })

      const pendingMessage = {
        id: 'local-pending-' + Date.now(),
        role: 'assistant',
        text: null,
        actions: [],
        pending: true,
      }

      this.messages.push(pendingMessage)

      this.loading = true

      const feedback = this.pendingFeedback
      this.pendingFeedback = []

      this.$http
        .$post('ai-assistant/agent/message', {
          agent: this.agent,
          context_type: this.contextType,
          context_id: this.contextId,
          customer_id: this.customerId,
          conversation_id: this.conversationId,
          message: text,
          document: this.workingDocument,
          feedback,
        })
        .then((response) => {
          this.conversationId = response.conversation_id
          pendingMessage.text = response.message
          pendingMessage.actions = response.actions || []
          pendingMessage.warnings = response.warnings || []
          pendingMessage.pending = false

          if (response.credits) {
            this.credits = response.credits
          }
        })
        .catch((error) => {
          // Kept visible (not filtered out) so the professional sees why
          // nothing came back and can retry, instead of the message just
          // vanishing — the global error snackbar (ErrorHandler) still
          // fires from the $http interceptor for the underlying cause.
          pendingMessage.pending = false
          pendingMessage.error = true
          pendingMessage.text = 'Não foi possível obter resposta da I.A. Tente novamente.'

          this.pendingFeedback = feedback.concat(this.pendingFeedback)
          this.form.instructions = this.form.instructions || text

          this.$emit('error', error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    // For a pending change, stale means the document has moved on from the
    // `before` this proposal was computed against. For an already-accepted
    // change, stale instead means the document no longer holds this
    // change's own `after` — i.e. a later change was accepted on the same
    // index since — so undoing it would revert to a snapshot that predates
    // that later, still-wanted edit instead of the one it's actually
    // wrapping.
    isStale(change) {
      const node = this.workingDocument[change.index]

      if (!node) {
        return true
      }

      const current = Array.isArray(node.answer) ? '' : node.answer || ''

      if (change.status === 'accepted') {
        return current !== (change.after || '')
      }

      return current !== (change.before || '')
    },

    // Finds the change by id across every message's actions (there's no
    // shortcut to its position since `change` here comes from the
    // renderedMessages copy, not from `this.messages` itself) and updates
    // its persisted status in place.
    setChangeStatus(changeId, status) {
      this.messages = this.messages.map((message) => ({
        ...message,
        actions: (message.actions || []).map((action) => ({
          ...action,
          changes: (action.changes || []).map((change) =>
            change.id === changeId ? { ...change, status } : change
          ),
        })),
      }))
    },

    handleDecide({ change, decision }) {
      const previous = change.status || 'pending'

      // A superseded accepted change (a later change was accepted on the
      // same index since) can't be safely undone: change.before predates
      // that later edit, so reverting to it would silently discard it. The
      // UI already hides "Desfazer" in this case — this is the last line
      // of defense.
      if (decision === 'pending' && previous === 'accepted' && this.isStale(change)) {
        return
      }

      this.setChangeStatus(change.id, decision)

      if (decision === 'pending' && previous === 'accepted') {
        this.workingDocument = applyChange(this.workingDocument, { index: change.index, after: change.before })
        this.emitApply()
      } else if (decision === 'accepted') {
        this.workingDocument = applyChange(this.workingDocument, change)
        this.emitApply()
      }

      this.pendingFeedback.push({ change_id: change.id, index: change.index, decision })

      this.persistDecision(change.id, decision)
    },

    // Fire-and-forget: an accept/reject/undo is already reflected locally
    // (setChangeStatus above), this just makes it survive a reload from
    // another device/browser. A failure here isn't worth blocking the UI
    // over — worst case the decision falls back to reappearing as pending.
    persistDecision(changeId, decision) {
      if (!this.conversationId) {
        return
      }

      this.$http
        .$post('ai-assistant/agent/decision', {
          conversation_id: this.conversationId,
          change_id: changeId,
          decision,
        })
        .catch(() => {})
    },

    emitApply() {
      this.$emit('apply', fromWireDocument(this.workingDocument, this.contextType, this.documentImages))
    },
  },
  beforeDestroy() {
    clearTimeout(this.clearChatConfirmTimer)
  },
}
</script>

<style scoped>
.agent-dialog-card {
  display: flex;
  flex-direction: column;
}

.agent-dialog-body {
  flex-grow: 1;
  overflow: hidden;
}
</style>
